import Map, { GeoJSON, Layer, LayerType, MapProps, WMS } from "@paradigmh2o/react-map";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Config from "../../../../../config/Config";
import WmgMapConfig from "./WmgMapConfig";
import inProgressWCM from '../../../../../images/InProgressWCMMarker.png';
import completeWCM from '../../../../../images/CompleteWCMMarker.png';
import newRedev from '../../../../../images/newRedevMarker.png';
import { ClassDisplayOptions, StatusDisplayOptions, WatershedReportPublicFacingConfig } from "../../../../../models/viewModels/WatershedReportPublicFacingConfigViewModel";
import { WatershedReportSnapshotBmp } from "../../../../../models/WatershedReportSnapshot";
import Extent from "@arcgis/core/geometry/Extent";
import { GeoJsonService } from "../../../../../services/GeoJsonService";
import { Legend } from "./Legend";
import { useAuth } from "react-oidc-context";
import { BmpClassId } from "../../../../../models/lutModels/LutBmpClass";
import { SidebarContext } from "../../../../../core/SidebarComponent";

export interface IProjectDetailsMapProps {
    wmgPublicInfo: WatershedReportPublicFacingConfig;
    wmgPublicData: Array<WatershedReportSnapshotBmp>;
    wmgId: number;
    width: number;
    handleDetailsClick: Function;
    useLiveData?: boolean;
}

enum GeoJsonEndpoints {
    GeoJsonSnapshotPublic = "GeoJsonSnapshotPublic", // For use on public facing - uses snapshot and only works when dashboard is published
    GeoJsonSnapshot = "GeoJsonSnapshot", // For using snapshot data on private-facing side. Ignores if the dashboard is published so that it can be previewed
    GeoJsonLive = "GeoJsonLive" // For using live data on private-facing side. Ignores if the dashboard is published so that it can be previewed
}

enum MapLayers {
    InProgressNewAndRedevelopment = "In Progress New & Redevelopment Projects",
    CompletedNewAndRedevelopment = "Completed New & Redevelopment Projects",
    InProgressWCMs = "In-Progress WCMs",
    CompletedWCMs = "Completed WCMs"
}

export const ProjectDetailsMap = (props: IProjectDetailsMapProps) => {
    const { handleDetailsClick } = props;
    const auth = useAuth();
    const geoJsonService = useMemo(() => new GeoJsonService(auth.user?.access_token), [auth]);
    const { setSidebarProjectInfoObj, dispatchSidebar } = useContext(SidebarContext);

    const sublayers = useMemo(() => {
        return [
            { name: "Storm Drains", viz: false },
            { name: "Channels(Drainage)", viz: true },
            { name: "Major Watersheds", viz: false },
            { name: "Safe Clean Water Program Watershed Areas", viz: false },
            { name: "Subwatersheds", viz: true },
            { name: "Jurisdictions", viz: true },
            { name: "Watershed Management Groups (WMGs)", viz: true }
        ].map((sublayer, i) => {
            return new WMS(
                i.toString(),
                sublayer.name,
                Config.map_connection_url,
                sublayer.viz,
                [
                    sublayer.name
                ],
            );
        });
    }, [])

    const endpoint: string = useMemo(() => {
        if (!auth.user?.access_token) {
            return GeoJsonEndpoints.GeoJsonSnapshotPublic
        }
        else {
            if (props.useLiveData) {
                return GeoJsonEndpoints.GeoJsonLive
            }
            else {
                return GeoJsonEndpoints.GeoJsonSnapshot
            }
        }
    }, [auth, props.useLiveData])

    const geoJsonLayers: GeoJSON[] = useMemo(() => {
        return [new GeoJSON(
            MapLayers.InProgressNewAndRedevelopment,
            "In Progress New and Redevelopment",
            `${Config.api_endpoint}GeoJson/${endpoint}/${props.wmgId}/${BmpClassId.NewAndRedevelopment}/1000`,
            true,
            false,
            {
                type: "simple",
                symbol: {
                    type: "picture-marker",
                    url: newRedev,
                    width: "18px",
                    height: "18px"
                }
            },
            null,
            null,
            null,
            auth.user?.access_token ? { Authorization: "Bearer " + auth.user?.access_token } : null
        ),
        new GeoJSON(
            MapLayers.CompletedNewAndRedevelopment,
            "Completed New and Redevelopment",
            `${Config.api_endpoint}GeoJson/${endpoint}/${props.wmgId}/${BmpClassId.NewAndRedevelopment}/1001`,
            true,
            false,
            {
                type: "simple",
                symbol: {
                    type: "picture-marker",
                    url: newRedev,
                    width: "18px",
                    height: "18px"
                }
            },
            null,
            null,
            null,
            auth.user?.access_token ? { Authorization: "Bearer " + auth.user?.access_token } : null
        ),
        new GeoJSON(
            MapLayers.InProgressWCMs,
            "In-Progress WCMs",
            `${Config.api_endpoint}GeoJson/${endpoint}/${props.wmgId}/${BmpClassId.WatershedControlMeasure}/1000`,
            true,
            true,
            {
                type: "simple",
                symbol: {
                    type: "picture-marker",
                    url: inProgressWCM,
                    width: "38px",
                    height: "38px"
                }
            },
            (e) => handleDetailsClick(e, props.wmgPublicData, setSidebarProjectInfoObj, dispatchSidebar),
            null,
            null,
            auth.user?.access_token ? { Authorization: "Bearer " + auth.user?.access_token } : null
        ),
        new GeoJSON(
            MapLayers.CompletedWCMs,
            "Completed WCMs",
            `${Config.api_endpoint}GeoJson/${endpoint}/${props.wmgId}/${BmpClassId.WatershedControlMeasure}/1001`,
            true,
            true,
            { //renderer
                type: "simple",
                symbol: {
                    type: "picture-marker",
                    url: completeWCM,
                    width: "38px",
                    height: "38px"
                }
            },
            (e) => handleDetailsClick(e, props.wmgPublicData, setSidebarProjectInfoObj, dispatchSidebar),
            null,
            null,
            auth.user?.access_token ? { Authorization: "Bearer " + auth.user?.access_token } : null
        )]
    }, [auth, endpoint, props.wmgPublicData, props.wmgId, handleDetailsClick, setSidebarProjectInfoObj, dispatchSidebar])

    const [layers, setLayers] = useState<Layer[]>([
        ...sublayers,
        ...Array.from(geoJsonLayers)
    ]);

    useEffect(() => {
        // Update layers with the new endpoint
        // Remove old ones and add new
        setLayers([...layers.filter(x => x.type !== LayerType.GeoJSON), ...Array.from(geoJsonLayers)]);
    }, [props.useLiveData])

    const filteredLayers: Layer[] = useMemo(() => {
        var tempFilteredLayers: Layer[] = [];

        if (props.wmgPublicInfo.projectDetailsMapClass === ClassDisplayOptions.WCMOnly && props.wmgPublicInfo.projectDetailsMapStatus === StatusDisplayOptions.CompleteOnly) {
            tempFilteredLayers.push(layers.find(x => x.id === MapLayers.CompletedWCMs));
        }
        else if (props.wmgPublicInfo.projectDetailsMapClass === ClassDisplayOptions.WCMOnly && props.wmgPublicInfo.projectDetailsMapStatus === StatusDisplayOptions.All) {
            tempFilteredLayers.push(layers.find(x => x.id === MapLayers.CompletedWCMs));
            tempFilteredLayers.push(layers.find(x => x.id === MapLayers.InProgressWCMs));
        }
        else if (props.wmgPublicInfo.projectDetailsMapClass === ClassDisplayOptions.All && props.wmgPublicInfo.projectDetailsMapStatus === StatusDisplayOptions.CompleteOnly) {
            tempFilteredLayers.push(layers.find(x => x.id === MapLayers.CompletedWCMs));
            tempFilteredLayers.push(layers.find(x => x.id === MapLayers.CompletedNewAndRedevelopment));
        }
        else if (props.wmgPublicInfo.projectDetailsMapClass === ClassDisplayOptions.All && props.wmgPublicInfo.projectDetailsMapStatus === StatusDisplayOptions.All) {
            tempFilteredLayers.push(layers.find(x => x.id === MapLayers.CompletedWCMs));
            tempFilteredLayers.push(layers.find(x => x.id === MapLayers.CompletedNewAndRedevelopment));
            tempFilteredLayers.push(layers.find(x => x.id === MapLayers.InProgressWCMs));
            tempFilteredLayers.push(layers.find(x => x.id === MapLayers.InProgressNewAndRedevelopment));
        }

        return tempFilteredLayers;
    }, [props.wmgPublicInfo.projectDetailsMapClass, props.wmgPublicInfo.projectDetailsMapStatus, layers])

    const toggleLayerVisibility = useCallback((layer: Layer) => {
        layers
            .filter(l => l === layer)
            .forEach(l => l.visible = !l.visible);
        setLayers([...layers]);
    }, [layers, setLayers]);

    const [extent, setExtent] = useState<Extent>();
    useEffect(() => {
        (async function() {
            var extent = await geoJsonService.getExtent(props.wmgId);
            if (extent.ymin === -extent.ymax) {
                return;
            }
            setExtent(extent);
        })();
    }, [props.wmgId, geoJsonService])

    return (
        <>
            <div className="map-box" style={{ marginTop: "-1.5rem" }}>
                <div
                    className="map"
                    style={{ height: "700px", width: "100%" }}>
                    <Map {...WmgMapConfig as MapProps}
                        layers={[...sublayers, ...filteredLayers]}
                        onMapViewCreated={() => { }}
                        zoomWidgetPosition="top-left"
                        extent={extent}
                        legend={(layers: Array<Layer>) => <Legend layers={layers} toggleVisibility={toggleLayerVisibility}
                            topUnhiddenLayers={filteredLayers.length} />} />
                </div>
            </div>
        </>
    );
}
